import React, { useState } from 'react';
import { 
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  BuildingStorefrontIcon,
  MapPinIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';
import { farmData } from '../data/mockData';

const CreateFarmModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    farmName: '',
    mobileNumber: '',
    subscriptionEndDate: '',
    location: '',
    size: '',
    season: 1
  });

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
    onClose();
    setFormData({
      farmName: '',
      mobileNumber: '',
      subscriptionEndDate: '',
      location: '',
      size: '',
      season: 1
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Farm</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Farm Name *
            </label>
            <input
              type="text"
              required
              value={formData.farmName}
              onChange={(e) => setFormData({...formData, farmName: e.target.value})}
              className="input"
              placeholder="Enter farm name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mobile Number *
            </label>
            <input
              type="tel"
              required
              value={formData.mobileNumber}
              onChange={(e) => setFormData({...formData, mobileNumber: e.target.value})}
              className="input"
              placeholder="Enter mobile number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location *
            </label>
            <input
              type="text"
              required
              value={formData.location}
              onChange={(e) => setFormData({...formData, location: e.target.value})}
              className="input"
              placeholder="Enter location"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Size (Acre) *
              </label>
              <input
                type="number"
                step="0.1"
                required
                value={formData.size}
                onChange={(e) => setFormData({...formData, size: e.target.value})}
                className="input"
                placeholder="0.0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Season
              </label>
              <input
                type="number"
                min="1"
                value={formData.season}
                onChange={(e) => setFormData({...formData, season: parseInt(e.target.value)})}
                className="input"
                placeholder="1"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subscription End Date *
            </label>
            <input
              type="date"
              required
              value={formData.subscriptionEndDate}
              onChange={(e) => setFormData({...formData, subscriptionEndDate: e.target.value})}
              className="input"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 btn-primary"
            >
              Create Farm
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Farm = () => {
  const [farms, setFarms] = useState(farmData);
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileSearch, setMobileSearch] = useState('');
  const [subscriptionEndDate, setSubscriptionEndDate] = useState('18 Sep 2025');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const handleCreateFarm = (formData) => {
    const newFarm = {
      id: `FARM${String(farms.length + 1).padStart(3, '0')}`,
      ...formData,
      dateCreated: new Date().toLocaleDateString('en-GB', { 
        weekday: 'short', 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      }),
      daysRemaining: Math.floor((new Date(formData.subscriptionEndDate) - new Date()) / (1000 * 60 * 60 * 24))
    };
    setFarms([...farms, newFarm]);
  };

  const filteredFarms = farms.filter(farm => {
    const matchesName = farm.farmName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMobile = farm.mobileNumber.includes(mobileSearch);
    return matchesName && matchesMobile;
  });

  const totalFarms = farms.length;
  const totalSize = farms.reduce((acc, farm) => acc + parseFloat(farm.size), 0);
  const avgSize = totalSize / totalFarms;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Farm</h1>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <PlusIcon className="h-4 w-4" />
          <span>Create Farm</span>
        </button>
      </div>

      {/* Search Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="relative">
          <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search a farm..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10"
          />
        </div>
        <div className="relative">
          <PhoneIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search mobile..."
            value={mobileSearch}
            onChange={(e) => setMobileSearch(e.target.value)}
            className="input pl-10"
          />
        </div>
        <input
          type="date"
          value="2025-09-18"
          onChange={(e) => setSubscriptionEndDate(e.target.value)}
          className="input"
        />
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card text-center">
          <div className="text-2xl font-bold text-gray-900">{totalFarms}</div>
          <div className="text-sm text-gray-600">Number of Farm</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-gray-900">{totalSize.toFixed(1)}</div>
          <div className="text-sm text-gray-600">Total Farm Size, Acre</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-gray-900">{avgSize.toFixed(1)}</div>
          <div className="text-sm text-gray-600">Avg. Farm Size, Acre</div>
        </div>
      </div>

      {/* Farms Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Farm Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size, Acre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Season
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mobile Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subscription End Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredFarms.map((farm) => (
                <tr key={farm.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {farm.dateCreated}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <BuildingStorefrontIcon className="h-5 w-5 text-primary-600 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{farm.farmName}</div>
                        <div className="text-sm text-gray-500">ID: {farm.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {farm.size}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {farm.season}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {farm.mobileNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-900">
                      <MapPinIcon className="h-4 w-4 text-gray-400 mr-1" />
                      {farm.location}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{farm.subscriptionEndDate}</div>
                    <div className={`text-xs ${
                      farm.daysRemaining > 30 ? 'text-green-600' : 
                      farm.daysRemaining > 7 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {farm.daysRemaining} more day(s)
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        className="text-blue-600 hover:text-blue-900"
                        title="View Details"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        className="text-gray-600 hover:text-gray-900"
                        title="Duplicate"
                      >
                        <DocumentDuplicateIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Results per page: 10
            </div>
            <div className="text-sm text-gray-700">
              1 - 1 out of {filteredFarms.length}
            </div>
          </div>
        </div>
      </div>

      <CreateFarmModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleCreateFarm}
      />
    </div>
  );
};

export default Farm;
